import { useState, useRef, useCallback, useEffect } from 'react'
import { RefreshCw, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

interface CaptchaWidgetProps {
  onVerified: (token: string) => void
}

interface CaptchaData {
  id: string
  backgroundImage: string
  templateImage: string
  backgroundImageWidth: number
  backgroundImageHeight: number
  templateImageWidth: number
  templateImageHeight: number
}

interface TrackPoint {
  x: number
  y: number
  type: 'down' | 'move' | 'up'
  t: number
}

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''

export function CaptchaWidget({ onVerified }: CaptchaWidgetProps) {
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null)
  const [loading, setLoading] = useState(false)
  const [templateX, setTemplateX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [displayWidth, setDisplayWidth] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<TrackPoint[]>([])
  const startTimeRef = useRef(0)
  const lastTrackXRef = useRef(0)
  const lastTrackTRef = useRef(0)
  const templateXRef = useRef(0)

  const BG_WIDTH = captcha?.backgroundImageWidth ?? 600
  const TEMPLATE_WIDTH = captcha?.templateImageWidth ?? 110

  // 后端 BasicCaptchaTrackValidator 相邻点间距硬上限 50
  const MAX_SEGMENT_PX = 40
  // 高刷新率一帧最多采样一次
  const MIN_SAMPLE_INTERVAL_MS = 16
  // 后端要求 >300ms，前端兜底 400ms
  const MIN_SLIDE_DURATION_MS = 400

  const getScale = useCallback(() => {
    if (!displayWidth || !captcha) return 1
    return captcha.backgroundImageWidth / displayWidth
  }, [displayWidth, captcha])

  const doFetchCaptcha = useCallback(async () => {
    try {
      const res = await fetch(`${API_ORIGIN}/admin/api/captcha/get`, { method: 'POST' })
      const json = await res.json()
      if (json.code === '10000' && json.data) {
        setCaptcha(json.data)
      } else {
        setErrorMessage(json.message || '验证码加载失败')
      }
    } catch {
      setErrorMessage('验证码加载失败')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadCaptcha = useCallback(() => {
    setLoading(true)
    setIsSuccess(false)
    setIsVerifying(false)
    setErrorMessage(null)
    setTemplateX(0)
    templateXRef.current = 0
    trackRef.current = []
    doFetchCaptcha()
  }, [doFetchCaptcha])

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch(`${API_ORIGIN}/admin/api/captcha/get`, { method: 'POST' })
        const json = await res.json()
        if (json.code === '10000' && json.data) {
          setCaptcha(json.data)
        } else {
          setErrorMessage(json.message || '验证码加载失败')
        }
      } catch {
        setErrorMessage('验证码加载失败')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  // 追加轨迹点，必要时插值避免相邻点 X 间距超过 MAX_SEGMENT_PX
  // currentX/currentT 已是在服务端原始尺寸下的坐标和时间
  const appendMovePoints = useCallback((currentX: number, currentT: number) => {
    const dx = currentX - lastTrackXRef.current
    const dt = currentT - lastTrackTRef.current
    if (Math.abs(dx) < 0.5 && dt < MIN_SAMPLE_INTERVAL_MS) return

    const segmentCount = Math.max(1, Math.ceil(Math.abs(dx) / MAX_SEGMENT_PX))
    for (let i = 1; i <= segmentCount; i++) {
      const ratio = i / segmentCount
      trackRef.current.push({
        x: Math.round(lastTrackXRef.current + dx * ratio),
        y: 0,
        type: 'move',
        t: Math.round(lastTrackTRef.current + dt * ratio),
      })
    }
    lastTrackXRef.current = currentX
    lastTrackTRef.current = currentT
  }, [])

  const handleStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (!containerRef.current || isSuccess) return

    setIsDragging(true)
    setTemplateX(0)
    templateXRef.current = 0
    trackRef.current = []
    lastTrackXRef.current = 0
    lastTrackTRef.current = 0
    startTimeRef.current = Date.now()

    // 起始点 X、Y 必须在后端要求的 [-10, 10] 原始尺寸范围内
    trackRef.current.push({ x: 0, y: 0, type: 'down', t: 0 })
  }, [isSuccess])

  const handleMove = useCallback((clientX: number) => {
    if (!isDragging || !containerRef.current || isSuccess || !captcha) return
    const rect = containerRef.current.getBoundingClientRect()
    const scale = getScale()
    const maxTravel = rect.width - (TEMPLATE_WIDTH / BG_WIDTH) * rect.width
    const x = Math.max(0, Math.min(clientX - rect.left, maxTravel))
    const relativeTime = Date.now() - startTimeRef.current

    setTemplateX(x)
    templateXRef.current = x

    if (relativeTime - lastTrackTRef.current < MIN_SAMPLE_INTERVAL_MS) return

    const scaledX = x * scale
    appendMovePoints(scaledX, relativeTime)
  }, [isDragging, isSuccess, captcha, getScale, BG_WIDTH, TEMPLATE_WIDTH, appendMovePoints])

  const handleEnd = useCallback(async () => {
    if (!isDragging || !captcha) return
    setIsDragging(false)

    const scale = getScale()
    const endX = templateXRef.current * scale
    const elapsed = Date.now() - startTimeRef.current

    // 追加最后一个 move 点
    appendMovePoints(endX, elapsed)

    // 如果滑动太快要等待到 400ms
    if (elapsed < MIN_SLIDE_DURATION_MS) {
      await new Promise((r) => setTimeout(r, MIN_SLIDE_DURATION_MS - elapsed))
    }

    const now = Date.now()
    const stopTime = now
    const relativeEndTime = now - startTimeRef.current

    // 追加 up 点
    trackRef.current.push({
      x: Math.round(endX),
      y: 0,
      type: 'up',
      t: Math.round(relativeEndTime),
    })

    if (!containerRef.current) return
    const containerRect = containerRef.current.getBoundingClientRect()
    const maxTravel = containerRect.width -
      (TEMPLATE_WIDTH / BG_WIDTH) * containerRect.width
    const progress = maxTravel > 0 ? templateXRef.current / maxTravel : 0

    // 必须拖超过 5% 且至少 10 个轨迹点
    if (progress <= 0.05 || trackRef.current.length < 10) return

    setIsVerifying(true)

    const track = {
      bgImageWidth: captcha.backgroundImageWidth,
      bgImageHeight: captcha.backgroundImageHeight,
      templateImageWidth: captcha.templateImageWidth,
      templateImageHeight: captcha.templateImageWidth,
      startTime: startTimeRef.current,
      stopTime,
      trackList: trackRef.current,
      left: Math.round(endX),
      top: 0,
    }

    try {
      const res = await fetch(`${API_ORIGIN}/admin/api/captcha/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: captcha.id, data: track }),
      })
      const json = await res.json()
      if (json.code === '10000' && json.data?.token) {
        setIsSuccess(true)
        setIsVerifying(false)
        await new Promise((r) => setTimeout(r, 900))
        onVerified(json.data.token)
      } else {
        toast.error('滑块验证失败，请重试', { duration: 1500 })
        setErrorMessage('验证失败')
        setIsVerifying(false)
        await new Promise((r) => setTimeout(r, 1000))
        loadCaptcha()
      }
    } catch {
      toast.error('滑块验证失败，请重试', { duration: 1500 })
      setErrorMessage('验证失败')
      setIsVerifying(false)
      await new Promise((r) => setTimeout(r, 1000))
      loadCaptcha()
    }
  }, [isDragging, captcha, getScale, appendMovePoints, onVerified, loadCaptcha, BG_WIDTH, TEMPLATE_WIDTH])

  // 拖拽期间使用 document 级鼠标事件，避免鼠标离开轨道元素时丢失
  useEffect(() => {
    if (!isDragging) return
    const onMouseMove = (e: MouseEvent) => { handleMove(e.clientX) }
    const onMouseUp = () => handleEnd()
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [isDragging, handleMove, handleEnd])

  const onImageLoad = useCallback(() => {
    if (containerRef.current) {
      setDisplayWidth(containerRef.current.getBoundingClientRect().width)
    }
  }, [])

  if (loading && !captcha) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!captcha && errorMessage) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-sm text-neutral-500">
        <AlertCircle className="h-4 w-4" />
        <span>{errorMessage}</span>
        <button onClick={loadCaptcha} className="text-primary-500 hover:underline">
          重试
        </button>
      </div>
    )
  }

  if (!captcha) return null

  const scale = getScale()
  const displayBgHeight = Math.round(captcha.backgroundImageHeight / scale)

  return (
    <div className="space-y-2">
      {/* 背景图 + 滑块 */}
      <div className="relative overflow-hidden rounded-lg bg-neutral-100" ref={containerRef}>
        {captcha.backgroundImage && (
          <img
            src={captcha.backgroundImage}
            alt="验证码"
            className="w-full select-none"
            draggable={false}
            onLoad={onImageLoad}
            style={{ display: 'block', height: displayBgHeight }}
          />
        )}
        {captcha.templateImage && (
          <img
            src={captcha.templateImage}
            alt="滑块"
            className="absolute top-0 select-none pointer-events-none"
            draggable={false}
            style={{
              left: `${templateX}px`,
              width: `${(captcha.templateImageWidth / captcha.backgroundImageWidth) * 100}%`,
              height: displayBgHeight,
            }}
          />
        )}

        {/* 验证中遮罩 */}
        {isVerifying && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full" />
          </div>
        )}

        {/* 验证成功遮罩 */}
        {isSuccess && (
          <div className="absolute inset-0 bg-black/35 flex items-center justify-center rounded-lg">
            <div className="bg-white rounded-xl px-5 py-4 shadow-lg text-center">
              <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-2">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-sm font-semibold text-neutral-900">验证成功</div>
            </div>
          </div>
        )}
      </div>

      {/* 滑块轨道 */}
      <div className="relative h-10 bg-neutral-100 rounded-lg select-none"
        onMouseDown={handleStart}
        onTouchStart={(e) => handleStart(e)}
        onTouchMove={(e) => {
          const touch = e.touches[0]
          handleMove(touch.clientX)
        }}
        onTouchEnd={handleEnd}
      >
        {/* 已滑动区域 */}
        <div
          className="absolute inset-y-0 left-0 rounded-lg transition-colors"
          style={{
            width: `${templateX + 40}px`,
            backgroundColor: isSuccess
              ? 'rgba(82, 196, 26, 0.2)'
              : 'rgba(255, 107, 0, 0.2)',
          }}
        />

        {/* 滑块按钮 */}
        <div
          className="absolute top-0 left-0 h-full w-10 rounded-lg bg-white shadow-md border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-neutral-50 transition-all"
          style={{
            transform: `translateX(${templateX}px)`,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>

      {/* 底部提示 */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-neutral-400">
          {isSuccess ? '验证通过' : errorMessage || '按住滑块，拖动拼图至缺口'}
        </span>
        <button
          type="button"
          onClick={loadCaptcha}
          className="flex items-center gap-1 text-neutral-400 hover:text-neutral-600"
        >
          <RefreshCw className="h-3 w-3" />
          刷新
        </button>
      </div>
    </div>
  )
}
